import type { BomAxis, BomPart, MassSource } from './bom'

/**
 * Der Filter über die Stückliste.
 *
 * WARUM ER IM KERN STEHT UND NICHT IN DER SEITE. Ein Filter sieht aus wie
 * Bedienzustand, und wäre es auch — wenn nicht am Ende eine SUMME stünde. Eine
 * gefilterte Liste hat eine andere Summe als die ganze, und wer die beiden
 * verwechselt, trägt eine Teilmasse als Gesamtmasse weiter. Das ist keine
 * Anzeigefrage mehr.
 *
 * Deshalb liefert `applyBomFilter` nicht nur die Zeilen, sondern auch, WIE VIEL
 * ausgeblendet ist. Die Oberfläche kann das dann nicht mehr übersehen, und der
 * Ausdruck trägt es mit.
 *
 * ER RECHNET NICHTS NEU. Die Zeilen kommen fertig aus `computeBom`; hier wird
 * ausgewählt und gezählt. Eine gefilterte Stückliste, die andere Massen zeigt
 * als die ungefilterte, wäre ein Fehler und keine Auswahl — die Massenschleife
 * läuft über die GANZE Maschine, auch wenn man gerade nur die Beine ansieht.
 */

export interface BomFilter {
  /** Gruppen, die gezeigt werden. Leer heisst: alle. */
  groups: readonly string[]
  /** Herkünfte, die gezeigt werden. Leer heisst: alle. */
  sources: readonly MassSource[]
  /** Freitext über Bezeichnung, Motor und Teilenummer. Leer heisst: alles. */
  text: string
  /** Nur Positionen ab dieser Einzelmasse, kg. 0 heisst: alle. */
  minMass: number
}

export const EMPTY_FILTER: BomFilter = { groups: [], sources: [], text: '', minMass: 0 }

/** Ist überhaupt etwas eingeschränkt? */
export const filterActive = (f: BomFilter): boolean =>
  f.groups.length > 0 || f.sources.length > 0 || f.text.trim() !== '' || f.minMass > 0

/**
 * Der Text, der eine Auswahl beschreibt — für Kopfzeile und Ausdruck.
 *
 * Er ist der Grund, warum es diese Funktion gibt: ein Ausdruck ohne die Angabe,
 * WAS gefiltert war, ist ein Blatt mit einer Summe, die niemand einordnen kann.
 */
export function filterSummary(f: BomFilter, sourceLabels: Record<MassSource, string>): string {
  const teile: string[] = []
  if (f.groups.length > 0) teile.push(`Gruppen: ${f.groups.join(', ')}`)
  if (f.sources.length > 0) {
    teile.push(`Herkunft: ${f.sources.map((s) => sourceLabels[s]).join(', ')}`)
  }
  if (f.text.trim() !== '') teile.push(`Suche: „${f.text.trim()}"`)
  if (f.minMass > 0) teile.push(`ab ${f.minMass.toFixed(2).replace('.', ',')} kg je Stück`)
  return teile.length > 0 ? teile.join(' · ') : 'ohne Einschränkung'
}

/** Was eine Zeile an Text hergibt, wonach man suchen würde. */
const haystack = (row: BomAxis | BomPart): string =>
  [
    row.label,
    row.group,
    row.note ?? '',
    'setup' in row ? (row.setup?.label ?? '') : '',
    'setup' in row ? (row.setup?.motorId ?? '') : '',
    row.evidence?.number ?? '',
  ]
    .join(' ')
    .toLowerCase()

function passes(row: BomAxis | BomPart, f: BomFilter): boolean {
  if (f.groups.length > 0 && !f.groups.includes(row.group)) return false
  if (f.sources.length > 0 && !f.sources.includes(row.massSource)) return false
  if (f.minMass > 0 && row.mass < f.minMass) return false
  const text = f.text.trim().toLowerCase()
  if (text !== '' && !haystack(row).includes(text)) return false
  return true
}

export interface FilteredBom {
  axes: BomAxis[]
  parts: BomPart[]
  /** Positionen in der Auswahl — Anzahl mitgezählt, nicht Zeilen. */
  count: number
  /** Positionen insgesamt. */
  countAll: number
  /** Masse der Auswahl, kg. */
  mass: number
  /** Masse aller Positionen, kg — OHNE Struktur, die ist ein Anteil. */
  massAll: number
  /** Wie viele Zeilen ausgeblendet sind. */
  hidden: number
}

/**
 * Wendet den Filter an und zählt, was er wegnimmt.
 *
 * `massAll` ist bewusst die Summe der POSITIONEN und nicht die Gesamtmasse der
 * Maschine: die enthält die Struktur, und die ist ein Anteil und keine Zeile.
 * Wer beide gleichsetzt, bekommt einen Anteil, der nie 100 % erreicht, und
 * sucht den Fehler an der falschen Stelle.
 */
export function applyBomFilter(
  axes: readonly BomAxis[],
  parts: readonly BomPart[],
  f: BomFilter,
): FilteredBom {
  const gewaehlteAchsen = axes.filter((a) => passes(a, f))
  const gewaehlteTeile = parts.filter((p) => passes(p, f))

  const summe = (rows: (BomAxis | BomPart)[]) =>
    rows.reduce((a, r) => a + r.mass * r.count, 0)
  const anzahl = (rows: readonly (BomAxis | BomPart)[]) => rows.reduce((a, r) => a + r.count, 0)

  return {
    axes: gewaehlteAchsen,
    parts: gewaehlteTeile,
    count: anzahl(gewaehlteAchsen) + anzahl(gewaehlteTeile),
    countAll: anzahl(axes) + anzahl(parts),
    mass: summe([...gewaehlteAchsen, ...gewaehlteTeile]),
    massAll: summe([...axes, ...parts]),
    hidden: axes.length + parts.length - gewaehlteAchsen.length - gewaehlteTeile.length,
  }
}

/** Alle Gruppen, in der Reihenfolge, in der sie in der Liste stehen. */
export function bomGroups(axes: readonly BomAxis[], parts: readonly BomPart[]): string[] {
  const out: string[] = []
  for (const row of [...axes, ...parts]) if (!out.includes(row.group)) out.push(row.group)
  return out
}
